import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories } from '@/mocks/movies';

const categoryNameMap: Record<string, string> = {
  'sci-fi': '科幻电影',
  action: '动作电影',
  comedy: '喜剧电影',
  romance: '爱情电影',
  horror: '恐怖电影',
  animation: '动画电影',
  suspense: '悬疑电影',
  war: '战争电影',
};

export default function MovieDetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!movie) {
    return (
      <>
        <SEO
          title="电影未找到 - 琪琪影院"
          description="抱歉，您查找的电影不存在。请浏览琪琪影院其他精彩电影。"
          keywords="琪琪影院,电影大全"
          canonicalUrl="https://qiqicinema.com"
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 flex flex-col items-center justify-center min-h-[60vh] px-4">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-secondary-100 mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground-900 mb-2">电影未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的电影不存在或已下架。</p>
            <Link to="/" className="px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const catSlug = movie.category;
  const catName = categoryNameMap[catSlug] || '分类';
  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const pageTitle = `${movie.title} (${movie.originalTitle}) ${movie.year} ${movie.resolution} - 琪琪影院高清在线看`;
  const pageDescription = `琪琪影院提供${movie.title}高清${movie.resolution}免费在线看。导演${movie.director}，主演${movie.cast.slice(0, 3).join('、')}。${movie.description.slice(0, 100)}`;
  const pageKeywords = `${movie.title},${movie.category},${movie.tags.join(',')},琪琪影院,高清电影在线看,${movie.resolution}`;

  const movieJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    alternateName: movie.originalTitle,
    description: movie.description,
    dateCreated: `${movie.year}`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map((c) => ({
      '@type': 'Person',
      name: c,
    })),
    image: movie.posterImage,
    genre: movie.tags,
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qiqicinema.com' },
      { '@type': 'ListItem', position: 2, name: catName, item: `https://qiqicinema.com/category/${catSlug}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalUrl={`https://qiqicinema.com/movie/${movie.id}`}
        ogType="video.movie"
        ogImage={movie.posterImage}
      />
      <script type="application/ld+json">{JSON.stringify(movieJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="mx-auto max-w-[1400px] px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catName, href: `/category/${catSlug}` },
                { label: movie.title },
              ]}
            />
          </div>

          <div className="bg-background-100 py-8 md:py-12">
            <div className="mx-auto max-w-[1400px] px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-shrink-0 mx-auto lg:mx-0">
                  <div className="w-48 md:w-56 aspect-[2/3] rounded-lg overflow-hidden border border-background-200/70">
                    <img
                      src={movie.posterImage}
                      alt={`${movie.title} (${movie.originalTitle}) 电影海报`}
                      title={`${movie.title} - ${movie.originalTitle}`}
                      className="w-full h-full object-cover object-top"
                      width="600"
                      height="900"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-2">
                    {movie.title}
                  </h1>
                  <p className="text-sm md:text-base text-foreground-500 mb-4">{movie.originalTitle}</p>

                  <div className="flex flex-wrap items-center gap-3 mb-6">
                    <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap">
                      {movie.rating} 分
                    </span>
                    <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-sm whitespace-nowrap">
                      {movie.resolution}
                    </span>
                    <span className="text-sm text-foreground-500 whitespace-nowrap">{movie.year}</span>
                    <span className="text-sm text-foreground-500 whitespace-nowrap">{movie.duration}</span>
                    <span className="text-sm text-foreground-500 whitespace-nowrap">{movie.region}</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-foreground-400 flex-shrink-0 whitespace-nowrap">导演：</span>
                      <span className="text-foreground-800 font-medium">{movie.director}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-foreground-400 flex-shrink-0 whitespace-nowrap">主演：</span>
                      <span className="text-foreground-800">{movie.cast.join(' / ')}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-foreground-400 flex-shrink-0 whitespace-nowrap">类型：</span>
                      <div className="flex gap-1.5 flex-wrap">
                        {movie.tags.map((tag) => (
                          <span key={tag} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-foreground-400 flex-shrink-0 whitespace-nowrap">地区：</span>
                      <span className="text-foreground-800">{movie.region}</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h2 className="font-heading font-semibold text-lg text-foreground-900 mb-2">剧情简介</h2>
                    <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <Link
                      to="/"
                      className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-fill mr-1.5"></i>在线观看
                    </Link>
                    <Link
                      to={`/category/${movie.category}`}
                      className="px-5 py-2.5 bg-secondary-100 text-secondary-900 rounded-full font-medium text-sm hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      更多{catName}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {relatedMovies.length > 0 && (
            <div className="bg-background-50 py-8 md:py-12">
              <div className="mx-auto max-w-[1400px] px-4 md:px-6">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-6">
                  <a href="#related" id="related">相关推荐</a>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}